import styled from 'styled-components';
import React from 'react';
import * as Sentry from '@sentry/react';
import { useNavigate } from 'react-router';

import { Route, Routes } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { getIsAuthed } from './utils/selectors';
import { ROUTES } from './constants/routes';
import { usePermissionedModes } from './hooks/user/useCurrentUser';
import { toCapitalCase } from './libs/nvstr-utils.es';
import { clearLastIntent, getLastIntent, setLastIntent } from './utils/intents';

import { Header } from './components/navigation';
import { Body } from './components/layout/Body';
import { Body1, Container, Page } from './libs/nvstr-common-ui.es';
import { Loading } from './components/UI/Loading';

import SignIn from './pages/SignIn';
import SignUp from './pages/SignUp';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Splash from './pages/Splash';
import ToolSelection from './pages/ToolSelection';
import DigitalAnalyst from './pages/DigitalAnalyst';
import ClosedBeta from './pages/ClosedBeta';
import CompanySnapshot from './pages/CompanySnapshot';
import Screener from './pages/Screener';
import { ThemeBuilder } from './pages/ThemeBuilder';
import DocumentManagement from './pages/DocumentManagement';
import StockDetail from './pages/StockDetail';
import { DocumentViewer } from './pages/DocumentViewer';
import EarningsCallSummaries from './pages/EarningsCallSummaries';
import ResearchScreener from './pages/PremiumScreener';
import ResearchDigitalAnalyst from './pages/ResearchDigitalAnalyst';
import FactorVisualization from './pages/FactorVisualization';
import FactorVisualizationChart from './pages/FactorVisualizationChart';
import FedDigitalAnalyst from './pages/FedDigitalAnalyst';
import FedGpt from './pages/Fedgpt';
import { DocumentSummary } from './pages/DocumentSummary';
import { LatestUpdatesAndNews } from './pages/LatestUpdatesAndNews';
import WebpageUploader from './pages/WebpageUploader';
import WarningIndicators from './pages/WarningIndicators';
import BullBearCases from './pages/BullBearCases';
import FedSentiment from './pages/FedSentiment';
import WebDigitalAnalyst from './pages/WebDigitalAnalyst';
import EvalTemplate from './pages/EvalTemplate';
import CustomSnapshot from './pages/CustomSnapshot';
import MacroEconomicSignals from './pages/MacroEconomicSignals';
import EconomicImpact from './pages/EconomicImpact';
import FedTableAnalysis from './pages/TableAnalysis/FedTableAnalysis';
import { TextSearch } from './pages/TextSearch';
import FAQ from './pages/FAQ';
import LenderEarningCallAnalysis from './pages/LenderEarningCallAnalysis';
import { AwaitDocProcessingToDigitalAnalystMode } from './pages/AwaitDocProcessingToDigitalAnalystMode';
import { AccountSettings } from './pages/AccountSettings';
import { ComplianceMode } from './pages/ComplianceMode';
import FedRecentDocs from './pages/FedRecentDocs';
import CreditAgreementAnalysis from './pages/TableAnalysis/CreditAgreementAnalysis';
import Watchlist from './pages/Watchlist';
import GenericTableAnalysis from './pages/TableAnalysis/GenericTableAnalysis';
import PPMTableAnalysis from './pages/TableAnalysis/PPMTableAnalysis';
import AccountInReviewPage from './pages/AccountInReviewPage';
import { QueryHistory } from './pages/QueryHistory';
import LiquidityTermsTableAnalysis from './pages/TableAnalysis/LiquidityTermsTableAnalysis';
import { logger } from './utils/usefulFuncs';
import GrowthEquityAnalysis from './pages/GrowthEquityAnalysis';
import { QueryEngine } from './pages/FlowQuery';
import QuickLook from './pages/QuickLook';
import ClientTableAnalysis from './pages/ClientTableAnalysis';
import ClientPeerTableAnalysis from './pages/ClientPeerTableAnalysis';
import ClientDigitalAnalyst from './pages/ClientDigitalAnalyst';
import CitationsGenerator from './pages/CitationsGenerator';
import AgentModePage from './pages/AgentModePage';
import { FlowAgentScript } from './pages/FlowAgentScript';
import ESGUpdatesPage from './pages/ESGUpdatesPage';
import DocumentCreatorPage, { DOCUMENT_TEMPLATE_MODE_IDS } from './pages/DocumentCreatorPage';

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

const buildRouteElement = (name, extraParams = {}) => {
  if (name === 'FedGpt') {
    return <FedGpt />;
  }
  if (name === 'FedSentiment') {
    return <FedSentiment />;
  }
  if (name === 'FedReleaseAnalysis') {
    return <FedTableAnalysis />;
  }
  if (name === 'NewDealTriage') {
    return <DocumentCreatorPage {...extraParams} />;
  }
  if (name === 'GrowthEquityMemoTemplate') {
    return <DocumentCreatorPage {...extraParams} />;
  }
  if (name === 'InvestmentMemoTemplate') {
    return <DocumentCreatorPage {...extraParams} />;
  }
  return null;
};

const PageWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
`;

const Redirect = ({ nonAuth }) => {
  const navigate = useNavigate();

  React.useEffect(() => {
    const intent = window.location.pathname + window.location.search;
    logger('intent', intent);
    setLastIntent(intent);
    navigate(nonAuth ? ROUTES.login : ROUTES.home);
  }, []);
  return null;
};

const ResetPasswordActiveSessionRedirect = () => {
  const navigate = useNavigate();
  React.useEffect(() => {
    navigate('/app/users/edit?tab=preferences');
  }, []);
  return null;
};

function routeName(s) {
  return s
    .split('/')
    .join('')
    .split('-')
    .map((s) => toCapitalCase(s))
    .join('');
}

const LoggedInRedirect = () => {
  const navigate = useNavigate();

  React.useEffect(() => {
    const prevIntent = getLastIntent();
    const route = prevIntent || ROUTES.home;
    logger('navigating after signin', { prevIntent, route });
    navigate(route);
    clearLastIntent();
  }, []);

  return null;
};

const LoadingApp = () => {
  const [error, setError] = React.useState(null);
  React.useEffect(() => {
    const timeout = setTimeout(() => {
      setError(true);
    }, 5000);

    return () => clearTimeout(timeout);
  });

  return (
    <PageWrapper>
      <Header />
      <Body withFooter>
        <Page width={'800px'}>
          {error ? (
            <Container bottom={140} top={16}>
              <Body1 bold>Something went wrong, refresh and try again.</Body1>
            </Container>
          ) : (
            <Loading />
          )}
        </Page>
      </Body>
    </PageWrapper>
  );
};

function AuthedRoutes() {
  const permissionedModes = usePermissionedModes();

  if (!permissionedModes) {
    return (
      <SentryRoutes>
        <Route path="/*" element={<LoadingApp />} />
      </SentryRoutes>
    );
  }
  return (
    <SentryRoutes>
      <Route path={ROUTES.queryEngine} element={<QueryEngine />} />

      <Route path={ROUTES.digitalAnalyst} element={<DigitalAnalyst />} />
      <Route path={ROUTES.researchDigitalAnalyst} element={<ResearchDigitalAnalyst />} />

      <Route path={ROUTES.fedLatestDocs} element={<FedRecentDocs />} />
      <Route path={ROUTES.fedDigitalAnalyst} element={<FedDigitalAnalyst />} />

      <Route path={ROUTES.website} element={<WebDigitalAnalyst />} />
      <Route path={ROUTES.lenderEarningsUpdates} element={<LenderEarningCallAnalysis />} />

      <Route path={ROUTES.companySnapshot} element={<CompanySnapshot />} />
      <Route path={ROUTES.customSnapshot} element={<CustomSnapshot />} />
      <Route path={ROUTES.warningIndicators} element={<WarningIndicators />} />
      <Route path={ROUTES.bullBearCases} element={<BullBearCases />} />

      <Route path={ROUTES.factor} element={<FactorVisualization />} />
      <Route path={ROUTES.factorVisual} element={<FactorVisualizationChart />} />

      <Route path={ROUTES.screener} element={<Screener />} />
      <Route path={ROUTES.researchScreener} element={<ResearchScreener />} />

      <Route path={ROUTES.latestEarningsUpdates} element={<EarningsCallSummaries />} />
      <Route path={ROUTES.latestUpdates} element={<LatestUpdatesAndNews />} />

      <Route path={ROUTES.stock} element={<StockDetail />} />
      <Route path={ROUTES.docSummary} element={<DocumentSummary />} />
      <Route path={ROUTES.watchlist} element={<Watchlist />} />

      <Route path={ROUTES.quickLook} element={<QuickLook />} />

      <Route path={ROUTES.compliance} element={<ComplianceMode />} />
      <Route path={ROUTES.queryHistory} element={<QueryHistory />} />

      <Route path={ROUTES.docProcessingToDA} element={<AwaitDocProcessingToDigitalAnalystMode />} />
      <Route path={ROUTES.docViewer} element={<DocumentViewer />} />
      <Route path={ROUTES.docManagement} element={<DocumentManagement />} />
      <Route path={ROUTES.webpageIngestion} element={<WebpageUploader />} />

      <Route path={ROUTES.textSearch} element={<TextSearch />} />

      <Route path={ROUTES.creditAgreementAnalysis} element={<CreditAgreementAnalysis />} />
      <Route path={ROUTES.liquidityTermsTableAnalysis} element={<LiquidityTermsTableAnalysis />} />
      <Route path={ROUTES.ppmTableAnalysis} element={<PPMTableAnalysis />} />
      <Route path={ROUTES.tableAnalysis} element={<GenericTableAnalysis />} />

      <Route path={ROUTES.macroEconomicSignals} element={<MacroEconomicSignals />} />
      <Route path={ROUTES.growthEquityAnalysis} element={<GrowthEquityAnalysis />} />
      <Route path={ROUTES.economicImpact} element={<EconomicImpact />} />

      <Route path={ROUTES.evaluationTemplate} element={<EvalTemplate />} />

      <Route path={ROUTES.accountSettings} element={<AccountSettings />} />

      <Route path={ROUTES.clientMatrix} element={<ClientTableAnalysis />} />
      <Route path={ROUTES.clientPeerMatrix} element={<ClientPeerTableAnalysis />} />
      <Route path={ROUTES.clientQuery} element={<ClientDigitalAnalyst />} />

      <Route path={ROUTES.home} element={<ToolSelection />} />
      <Route path={ROUTES.login} element={<LoggedInRedirect />} />
      <Route path={ROUTES.resetPassword} element={<ResetPasswordActiveSessionRedirect />} />
      <Route path={ROUTES.themeBuilder} element={<ThemeBuilder />} />
      <Route path={ROUTES.faq} element={<FAQ />} />

      <Route
        path={ROUTES.newDealTriage}
        element={<DocumentCreatorPage modeId={DOCUMENT_TEMPLATE_MODE_IDS.new_deal_triage} />}
      />
      <Route path={ROUTES.inReview} element={<AccountInReviewPage />} />

      <Route path={ROUTES.esgUpdates} element={<ESGUpdatesPage />} />

      <Route path={ROUTES.citations} element={<CitationsGenerator />} />
      <Route path={ROUTES.agentScriptEditor} element={<AgentModePage />} />
      <Route path={ROUTES.flowAgentScriptEditor} element={<FlowAgentScript />} />

      {permissionedModes.map((m) => {
        const { route, mode_type } = m;
        if (!route) return null;
        const RouteName = routeName(route);
        return <Route path={route} element={buildRouteElement(RouteName, { modeId: mode_type })} key={route} />;
      })}

      {/*<Route path={'/sandbox'} element={<Sandbox />} />*/}
      <Route path="/closed-beta" element={<ClosedBeta />} />
      <Route path="/*" element={<Redirect />} />
    </SentryRoutes>
  );
}

function NonAuthedRoutes() {
  return (
    <SentryRoutes>
      <Route path={ROUTES.faq} element={<FAQ />} />

      <Route path={ROUTES.login} exact element={<SignIn />} />
      <Route path={ROUTES.signup} exact element={<SignUp />} />
      <Route path={ROUTES.forgotPassword} exact element={<ForgotPassword />} />
      <Route path={ROUTES.resetPassword} exact element={<ResetPassword />} />
      <Route path={ROUTES.splash} element={<Splash />} />
      <Route path="/*" element={<Redirect nonAuth />} />
    </SentryRoutes>
  );
}

export default function Router() {
  const isAuthed = useSelector(getIsAuthed);

  if (isAuthed) {
    return <AuthedRoutes />;
  }
  return <NonAuthedRoutes />;
}
